import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2, Send, ShieldCheck, Users, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import Reveal from '../../components/public/Reveal';
import api from '../../lib/api';
import { DEPARTMENTS, PROFESSIONAL_TYPES } from '../../lib/constants';

const PERKS = [
  { icon: ShieldCheck, text: 'Respaldo de la institución más representativa del sector' },
  { icon: Users, text: 'Acceso a la red nacional de socios y oportunidades' },
  { icon: QrCode, text: 'Credencial digital con QR verificable' },
];

const EMPTY = { full_name: '', email: '', whatsapp: '', city: '', professional_type: '', company: '', message: '' };

export default function HagaseSocio() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/membership-requests', form);
      setSent(true);
      toast.success('Solicitud enviada correctamente.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la solicitud. Inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    'w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all placeholder:text-slate-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20';

  return (
    <div className="hero-grain relative min-h-screen bg-cnib-navy pb-24 pt-32 md:pt-40">
      <div className="absolute inset-0 bg-gradient-to-br from-cnib-navy via-cnib-navy to-cnib-blue/30" />
      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-orange-500">Hágase socio</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
            Únete a la red inmobiliaria más grande de Bolivia
          </h1>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-slate-300">
            Completa tu solicitud y un asesor de la Cámara te contactará para activar tu membresía.
          </p>
          <ul className="mt-10 space-y-5">
            {PERKS.map((perk, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-500/15">
                  <perk.icon className="h-5 w-5 text-orange-500" />
                </span>
                <p className="pt-2 text-slate-300">{perk.text}</p>
              </li>
            ))}
          </ul>
          <p className="mt-10 text-sm text-slate-500">
            ¿Ya eres socio?{' '}
            <Link to="/login" className="font-bold text-orange-400 hover:text-orange-300" data-testid="hagase-socio-login-link">
              Inicia sesión aquí
            </Link>
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-md md:p-10">
            {sent ? (
              <div className="flex flex-col items-center py-14 text-center" data-testid="membership-success">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-bold text-white">Solicitud recibida</h3>
                <p className="mt-3 max-w-sm leading-relaxed text-slate-300">
                  Tu solicitud está en revisión. Te enviamos una confirmación por correo y un asesor te contactará por
                  WhatsApp.
                </p>
                <Link
                  to="/"
                  className="mt-8 rounded-full bg-orange-500 px-8 py-3 font-bold text-white transition-colors hover:bg-orange-600"
                  data-testid="membership-success-home"
                >
                  Volver al inicio
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-200">Nombre completo *</label>
                  <input name="full_name" value={form.full_name} onChange={handleChange} required minLength={3} className={inputCls} placeholder="Ej. María Fernanda Rojas" data-testid="membership-name-input" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-200">Correo electrónico *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputCls} placeholder="tucorreo@ejemplo.com" data-testid="membership-email-input" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-200">WhatsApp *</label>
                    <input name="whatsapp" value={form.whatsapp} onChange={handleChange} required minLength={6} className={inputCls} placeholder="+591 700 00000" data-testid="membership-whatsapp-input" />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-200">Departamento *</label>
                    <select name="city" value={form.city} onChange={handleChange} required className={`${inputCls} [&>option]:text-slate-900`} data-testid="membership-city-select">
                      <option value="">Selecciona…</option>
                      {DEPARTMENTS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-200">Perfil profesional *</label>
                    <select name="professional_type" value={form.professional_type} onChange={handleChange} required className={`${inputCls} [&>option]:text-slate-900`} data-testid="membership-type-select">
                      <option value="">Selecciona…</option>
                      {PROFESSIONAL_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-200">Empresa / Inmobiliaria</label>
                  <input name="company" value={form.company} onChange={handleChange} className={inputCls} placeholder="Opcional" data-testid="membership-company-input" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-200">Mensaje</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={3} className={inputCls} placeholder="Cuéntanos sobre tu experiencia en el sector (opcional)" data-testid="membership-message-input" />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="membership-form-submit-button"
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 py-4 font-bold text-white shadow-xl shadow-orange-500/25 transition-all duration-300 hover:bg-orange-600 disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                  {sending ? 'Enviando solicitud…' : 'Enviar solicitud de membresía'}
                </button>
                <p className="text-center text-xs text-slate-500">
                  Al enviar aceptas ser contactado por la CNIB. Tus datos están protegidos.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
